import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Sparkles, TrendingUp, Eye, Lock, MessageCircle, BadgeCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, GradientButton } from "@/screens/_shared";
import { addTransaction } from "@/lib/monetizationStore";
import { initPaystackPayment, isPaystackConfigured } from "@/lib/paystack";

const PLANS = [
    { id: "monthly", label: "Monthly", price: 2500,  period: "/ month", tag: null },
    { id: "yearly",  label: "Yearly",  price: 24000, period: "/ year",  tag: "Save 20%" },
];

const BENEFITS = [
    { icon: TrendingUp,   text: "Boosted visibility in Discovery" },
    { icon: Eye,          text: "Priority placement in Near Me" },
    { icon: BadgeCheck,   text: "Verified premium profile badge" },
    { icon: Sparkles,     text: "Advanced profile insights" },
    { icon: Lock,         text: "Increased service & event exposure" },
    { icon: MessageCircle,text: "Priority support access" },
];

const Premium = () => {
    const navigate = useNavigate();
    const { user, updateUser } = useAuth();
    const [plan, setPlan] = useState("monthly");
    const [busy, setBusy] = useState(false);

    const currentPlan = PLANS.find((p) => p.id === plan);
    const isPremium = !!user?.premium;
    const [paymentError, setPaymentError] = useState(null);

    const completePremium = (reference) => {
        const now = Date.now();
        const daysMs = plan === "yearly" ? 365 : 30;
        const expiresAt = now + daysMs * 24 * 60 * 60 * 1000;
        updateUser({
            premium: true,
            premiumPlan: plan,
            premiumSince: new Date(now).toISOString(),
            premiumExpiresAt: new Date(expiresAt).toISOString(),
        });
        if (user) {
            addTransaction(user.id, {
                type: "debit",
                kind: "premium",
                title: `Premium ${currentPlan.label}`,
                amount: currentPlan.price,
                reference: reference || null,
            });
        }
        setBusy(false);
        navigate("/premium/success", { replace: true });
    };

    const handleUpgrade = async () => {
        if (!currentPlan) return;
        setPaymentError(null);
        if (!isPaystackConfigured()) {
            setPaymentError("Paystack is not configured yet.");
            return;
        }
        if (!user?.email) {
            setPaymentError("Add an email to your profile first — Paystack needs it.");
            return;
        }
        setBusy(true);
        const reference = `NEA-PRM-${Date.now()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
        const res = await initPaystackPayment({
            email: user.email,
            amountNgn: currentPlan.price,
            reference,
            metadata: { purpose: "premium", plan: currentPlan.id, userId: user.id },
            onSuccess: (paidRef) => completePremium(paidRef),
            onClose: () => setBusy(false),
        });
        if (!res.ok) {
            setBusy(false);
            setPaymentError("Could not start payment. Please try again.");
        }
    };

    return (
        <div className="screen" data-testid="premium-screen">
            <PageHeader title="Neyra Premium" />
            <div className="flex-1 overflow-y-auto no-scrollbar px-5 py-5">
                <div
                    className="rounded-3xl p-5 text-primary-foreground shadow-card"
                    style={{
                        background:
                            "linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary) / 0.72) 100%)",
                    }}
                >
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/20 text-[11px] font-semibold backdrop-blur">
                        <Sparkles size={11} />
                        PREMIUM
                    </div>
                    <h2 className="mt-3 font-display text-[22px] font-semibold leading-tight">
                        Stand out. Get booked. Grow faster.
                    </h2>
                    <p className="mt-1.5 text-[13px] text-white/85">
                        Unlock the tools the pros use on Neyra.
                    </p>
                </div>

                <ul className="mt-5 space-y-2.5">
                    {BENEFITS.map((b, i) => {
                        const Icon = b.icon;
                        return (
                            <li key={i} className="flex items-center gap-3 p-3 rounded-2xl bg-card border border-border shadow-soft">
                                <span className="h-9 w-9 rounded-xl flex items-center justify-center bg-primary/10 text-primary">
                                    <Icon size={16} strokeWidth={2.3} />
                                </span>
                                <span className="text-[13.5px] font-medium">{b.text}</span>
                                <Check size={16} className="ml-auto text-primary" />
                            </li>
                        );
                    })}
                </ul>

                <h3 className="mt-6 text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold mb-2">
                    Choose a plan
                </h3>
                <div className="grid grid-cols-2 gap-3">
                    {PLANS.map((p) => (
                        <button
                            key={p.id}
                            data-testid={`premium-plan-${p.id}`}
                            onClick={() => setPlan(p.id)}
                            className={[
                                "relative p-4 rounded-2xl border text-left transition-[transform,border-color,box-shadow] duration-200 active:scale-[0.98]",
                                plan === p.id
                                    ? "border-primary bg-primary/5 shadow-[0_0_0_3px_hsl(var(--primary)/0.15)]"
                                    : "border-border bg-card shadow-soft",
                            ].join(" ")}
                        >
                            {p.tag && (
                                <span className="absolute -top-2 right-3 px-2 py-0.5 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold shadow-soft">
                                    {p.tag}
                                </span>
                            )}
                            <div className="font-display text-[14px] font-semibold">{p.label}</div>
                            <div className="mt-1">
                                <span className="font-display text-[20px] font-semibold tracking-tight">
                                    ₦{p.price.toLocaleString()}
                                </span>
                                <span className="text-[11.5px] text-muted-foreground ml-1">{p.period}</span>
                            </div>
                        </button>
                    ))}
                </div>

                {isPremium && (
                    <div className="mt-5 rounded-2xl border border-primary/30 bg-primary/5 text-primary px-4 py-3 text-[12.5px]">
                        You're already Premium — thanks for supporting Neyra!
                    </div>
                )}
            </div>

            <div className="px-5 pt-3 pb-[max(20px,env(safe-area-inset-bottom))] border-t border-border/60 bg-background space-y-2">
                {!isPaystackConfigured() && !isPremium && (
                    <div
                        data-testid="premium-paystack-warning"
                        className="rounded-xl border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 text-[12px] font-medium px-3.5 py-2"
                    >
                        Paystack is not configured yet.
                    </div>
                )}
                {paymentError && (
                    <p
                        data-testid="premium-payment-error"
                        className="text-[12px] text-destructive font-medium text-center"
                    >
                        {paymentError}
                    </p>
                )}
                <GradientButton
                    testId="premium-upgrade-btn"
                    onClick={handleUpgrade}
                    disabled={busy || isPremium}
                >
                    {busy
                        ? "Processing…"
                        : isPremium
                        ? "Already Premium"
                        : `Upgrade — ₦${currentPlan.price.toLocaleString()}`}
                </GradientButton>
                <p className="text-[11px] text-muted-foreground text-center mt-2">
                    Secure checkout powered by Paystack.
                </p>
            </div>
        </div>
    );
};

export default Premium;
